import { type ReactNode } from 'react';

import { cn } from '@/lib/tw-merge/cn';

interface LogoMarqueeProps {
  /** Logo row content. Duplicated internally for seamless looping. */
  children: ReactNode;
  /** Seconds per full cycle. Lower = faster. Defaults to 30s. */
  durationSeconds?: number;
  /** Tailwind gap class applied both inside each track and between the duplicated tracks. */
  gapClass?: string;
  /** Scroll direction; defaults to 'left'. */
  direction?: 'left' | 'right';
  /** Pause the animation while hovering the marquee. */
  pauseOnHover?: boolean;
  /**
   * Edge fade on the scroll viewport. Use `trailing` when a fixed badge
   * sits flush on the left so only the right edge soft-fades.
   */
  edgeFade?: 'both' | 'trailing';
  /** Extra classes for the outer viewport. */
  className?: string;
}

/**
 * Horizontal infinite marquee. Renders the children twice in a single flex
 * row and translates it by -50%, yielding a seamless loop (both halves are
 * identical so the wrap is invisible).
 *
 * Usage:
 *   <LogoMarquee gapClass="gap-14" durationSeconds={40}>
 *     <img ... /> <img ... /> ...
 *   </LogoMarquee>
 */
export default function LogoMarquee({
  children,
  durationSeconds = 30,
  gapClass = 'gap-14',
  direction = 'left',
  pauseOnHover = true,
  edgeFade = 'both',
  className,
}: LogoMarqueeProps) {
  const animationName =
    direction === 'left' ? 'logo-marquee-left' : 'logo-marquee-right';

  const maskClass =
    edgeFade === 'trailing'
      ? '[mask-image:linear-gradient(to_right,black_0%,black_88%,transparent_100%)]'
      : '[mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]';

  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden',
        maskClass,
        className
      )}
    >
      <style>{`
        @keyframes logo-marquee-left {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        @keyframes logo-marquee-right {
          from { transform: translate3d(-50%, 0, 0); }
          to   { transform: translate3d(0, 0, 0); }
        }
      `}</style>

      <div
        className={cn('flex w-max flex-nowrap', gapClass)}
        style={{
          animation: `${animationName} ${durationSeconds}s linear infinite`,
          animationPlayState: 'running',
        }}
        onMouseEnter={e => {
          if (pauseOnHover) e.currentTarget.style.animationPlayState = 'paused';
        }}
        onMouseLeave={e => {
          if (pauseOnHover)
            e.currentTarget.style.animationPlayState = 'running';
        }}
      >
        <div
          className={cn('flex shrink-0 flex-nowrap items-center', gapClass)}
          aria-hidden={false}
        >
          {children}
        </div>
        <div
          className={cn('flex shrink-0 flex-nowrap items-center', gapClass)}
          aria-hidden
        >
          {children}
        </div>
      </div>
    </div>
  );
}
